import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, Loader, ArrowRight, RefreshCcw, Eye, BarChart2, Layout, Globe, Maximize2, Minimize2, X } from 'lucide-react';
import axios from 'axios';

const API_URL = 'https://plansync-backend-234288438538.us-central1.run.app/chatuserguide';

const suggestions = [
  { icon: Eye, text: "How do I add in a new plan touchpoint?" },
  { icon: BarChart2, text: "I have an email chain with a client. How do I forward that email chain so that it automatically ends adds new touchpoints for me?" },
  { icon: Layout, text: "How do I see what requirements I have due this month?" },
  { icon: Globe, text: "How do I add in information from my plan adoption agreement?" },
];

const formatMessage = (content) => {
  // Split the content by newlines and filter out empty strings
  const paragraphs = content.split('\n').filter(line => line.trim() !== '');
  
  return paragraphs.map((paragraph, index) => (
    <p key={index} className={index > 0 ? 'mt-2' : ''}>
      {paragraph}
    </p>
  ));
};

const ChatbotWidget = ({ isOpen, onClose }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [userInput, setUserInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const chatContainerRef = useRef(null);
  const [conversationHistory, setConversationHistory] = useState([]);

  const handleUserInput = (e) => setUserInput(e.target.value);
  const toggleExpand = () => setIsExpanded(!isExpanded);

  const sendMessageToAPI = async (message) => {
    try {
      const response = await axios.post(API_URL, {
        query: message,
        conversation_history: conversationHistory
      });

      // Update conversation history with the new exchange
      setConversationHistory(response.data.conversation_history);
      
      return response.data.response;
    } catch (error) {
      console.error('Error sending message to API:', error);
      return 'Sorry, I encountered an error. Please try again.';
    }
  };

  const handleSendMessage = async (message = userInput) => {
    if (message.trim() === '') return;

    // Add user message to chat
    const userMessage = { type: 'user', content: message };
    setChatMessages(prev => [...prev, userMessage]);
    setUserInput('');
    setIsTyping(true);

    try {
      // Get response from API
      const botResponse = await sendMessageToAPI(message);
      
      // Add bot response to chat
      setChatMessages(prev => [...prev, { type: 'bot', content: botResponse }]);
    } catch (error) {
      // Handle error
      setChatMessages(prev => [...prev, { 
        type: 'bot', 
        content: 'Sorry, I encountered an error. Please try again.' 
      }]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleClearMessages = () => {
    setChatMessages([]);
    setConversationHistory([]); // Clear conversation history as well
  };

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [chatMessages]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscapeKey);

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen, onClose]);

  const containerVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: { 
      opacity: 1,
      scale: 1,
      transition: { duration: 0.3, ease: "easeOut" }
    },
    exit: { opacity: 0, scale: 0.95, transition: { duration: 0.2, ease: "easeIn" } }
  };

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className={`fixed z-50 transition-all duration-300 ease-in-out z-[99]
              ${isExpanded 
                ? 'bottom-4 right-4 w-[800px] h-[700px]'
                : 'inset-0 md:inset-auto md:bottom-4 md:right-4 md:w-96 md:h-[600px]'}
              bg-white flex flex-col overflow-hidden
              rounded-2xl shadow-2xl border border-gray-200`}
          >
            <div className="bg-gradient-to-bl from-sky-400 to-blue-800 p-4 flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <MessageCircle className="text-white" />
                <h4 className="text-white font-semibold text-lg">User Guide Chatbot</h4>
              </div>
              <div className="flex items-center space-x-2">
                <button onClick={handleClearMessages} className="text-white hover:text-blue200 transition duration-200">
                  <RefreshCcw className="h-5 w-5" />
                </button>
                <button onClick={toggleExpand} className="text-white hover:text-blue200 transition duration-200 hidden md:block">
                  {isExpanded ? <Minimize2 className="h-5 w-5" /> : <Maximize2 className="h-5 w-5" />}
                </button>
                <button onClick={onClose} className="text-white hover:text-blue200 transition duration-200">
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            <div ref={chatContainerRef} className="flex-grow p-4 overflow-y-auto space-y-4 bg-gray-50">
              {chatMessages.length === 0 ? (
                <div className="space-y-4">
                  <p className="text-center text-gray-600">Here are some example questions you can ask:</p>
                  <div className={`grid ${isExpanded ? 'grid-cols-2' : 'grid-cols-1'} gap-4`}>
                    {suggestions.map((suggestion, index) => (
                      <motion.button
                        key={index}
                        className="bg-white text-left text-gray-800 p-4 rounded-xl shadow-sm hover:shadow-md transition-all duration-200 ease-in-out flex items-start space-x-3 border border-gray-200"
                        onClick={() => handleSendMessage(suggestion.text)}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <suggestion.icon className="w-5 h-5 text-blue-500 flex-shrink-0" />
                        <span className="text-sm">{suggestion.text}</span>
                      </motion.button>
                    ))}
                  </div>
                </div>
              ) : (
                <div className={`mx-auto ${isExpanded ? 'max-w-2xl' : ''}`}>
                  <AnimatePresence>
                    {chatMessages.map((message, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'} mb-4`}
                      >
                        <div className={`${isExpanded ? 'max-w-[600px]' : 'max-w-xs'} px-4 py-2 rounded-2xl border border-gray-200 shadow-xl ${
                          message.type === 'user' 
                            ? 'bg-gradient-to-bl from-sky-400 to-blue-800 text-white' 
                            : 'bg-white text-gray-800'
                        }`}>
                          <div className="text-sm font-medium break-words">
                            {formatMessage(message.content)}
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </div>
              )}
              {isTyping && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex items-center text-blue-600 bg-white rounded-full px-4 py-2 shadow-sm w-max"
                >
                  <Loader className="w-4 h-4 mr-2 animate-spin" />
                  <span className="text-sm font-medium">Finding your answer...</span>
                </motion.div>
              )}
            </div>
            <div className="p-4 bg-white border-t border-gray-200">
              <div className={`flex items-center bg-gray-100 rounded-full overflow-hidden shadow-inner ${isExpanded ? 'max-w-2xl mx-auto' : ''}`}>
                <input 
                  type="text" 
                  value={userInput}
                  onChange={handleUserInput}
                  onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                  placeholder="Type your question here..." 
                  className="flex-grow px-4 py-2 bg-transparent focus:outline-none text-gray-700 text-sm"
                />
                <button 
                  onClick={() => handleSendMessage()}
                  className="p-2 transition-all duration-300 ease-in-out hover:bg-indigo-100"
                >
                  <ArrowRight className="w-5 h-5 text-blue-600" />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ChatbotWidget;
